import { Badge } from '@liveauctioneers/hammer-ui-core/badge';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import loadable from '@loadable/component';
import { memo, useEffect, SyntheticEvent } from 'react';

import ConsoleSocialBadge from '@/components/ConsoleSocial/ConsoleSocialBadge';
import ItemSaveContainer from '@/components/ItemSave/ItemSaveContainer';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCatalogAbsenteeBidders } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchLiveCatalogRegistrationByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import {
    getLiveCatalogBidders,
    getLiveCatalogCurrentItemId,
    getLiveCatalogCurrentItemIndex,
    getLiveCatalogPaused,
} from '@/redux/modules/console';
import {
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryIsCatalogOnly,
    getItemSummaryPromoted,
    getItemSummarySaleStartTs,
    getItemSummarySellerId,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { shouldShowLive } from '@/utils/live';
import noop from '@/utils/noop';

import { CardCta } from './CardCta';
import ItemCardMenu from './components/Menu/ItemCardMenu';
import { DetailsRow } from './DetailsRow';
import { ImageRow } from './ImageRow';
import { PriceRow } from './PriceRow';
import { TitleRow } from './TitleRow';

/** Establish connection to real-time auction events, only on the client-side */
const LiveAuctionEventsSubscriber = loadable(
    /* #__LOADABLE__ */
    () => import('@/components/LiveEventSubscribers/LiveAuctionEventsSubscriber'),
    {
        ssr: false,
    }
);

type Props = {
    allowBidding?: boolean;
    className?: string;
    hideLiveButton?: boolean;
    hideLotNumbers?: boolean;
    hideSeller?: boolean;
    itemId: number;
    noPubNub?: boolean;
    noWidthLimit?: boolean;
    openOnBlank?: boolean;
    showBanner?: boolean;
    showBidStatus?: boolean;
    showCta?: boolean;
    showDate?: boolean;
    showEstimate?: boolean;
    showImageBorder?: boolean;
    showImageWithHover?: boolean;
    showSoldBadge?: boolean;
    showSubMenuTrigger?: boolean;
    style?: { [attribute: string]: string | number };
    trackClick?: (event: SyntheticEvent) => void;
};

const ItemCardComponent = ({
    allowBidding,
    className = '',
    hideLiveButton,
    hideLotNumbers,
    hideSeller,
    itemId,
    noPubNub,
    noWidthLimit,
    openOnBlank,
    showBanner,
    showBidStatus,
    showCta,
    showDate,
    showEstimate,
    showImageBorder,
    showImageWithHover,
    showSoldBadge,
    showSubMenuTrigger,
    style = {},
    trackClick = noop,
}: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const paused = useAppSelector((state) => getLiveCatalogPaused(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const isLotOpen = currentItem === itemId;
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const showSocial = allowBidding && showLive;
    const viewers = bidders + absenteeBidders || 1;

    const previousCatalogStatus = usePrevious(catalogStatus);

    useEffect(() => {
        if (previousCatalogStatus !== 'live' && catalogStatus === 'live') {
            if (allowBidding) {
                dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
                dispatch(fetchCatalogPresence(catalogId));
            }
            dispatch(
                fetchLiveCatalogRegistrationByIdIfNeeded({
                    catalogId,
                    sellerId,
                })
            );
        }
    }, [allowBidding, catalogId, catalogStatus, sellerId, dispatch, previousCatalogStatus]);

    return (
        <div
            className={cn(
                'relative block transition-all',
                { 'min-w-[150px]': showSocial },
                {
                    'min-[501px]:bg-background-subdued min-[501px]:rounded': isLotOpen && !noWidthLimit,
                },
                {
                    '[&_div]:!text-transparent [&_div]:[text-shadow:0_0_10px_rgba(33,22,20,0.4)] [&_h4]:!text-transparent [&_h4]:[text-shadow:0_0_10px_rgba(33,22,20,0.4)] [&_img]:blur-[5px] [&_img]:![filter:blur(5px)] [&_span]:!text-transparent [&_span]:[text-shadow:0_0_10px_rgba(33,22,20,0.4)]':
                        !title,
                },
                className
            )}
            data-testid="item-card"
            key={`${itemId}-noWidth-${noWidthLimit}`}
            onClick={trackClick}
            style={style}
        >
            {comingUp && allowBidding && !noPubNub && (
                <LiveAuctionEventsSubscriber
                    catalogId={catalogId}
                    limitMessagesFilter="itemCard"
                />
            )}
            {promoted && (
                <Badge
                    className={cn('absolute left-0 top-0', {
                        'left-xs top-xs': !noWidthLimit,
                    })}
                    color="neutral"
                    size="sm"
                >
                    {formatMessage(['featured'])}
                </Badge>
            )}
            <div
                className={cn('relative print:flex print:flex-row', {
                    'w-fit': noWidthLimit,
                })}
            >
                <section
                    className={cn('absolute right-2xs top-2xs z-[2] max-h-[30px] p-2xs print:hidden', {
                        'right-xs top-xs': !noWidthLimit,
                    })}
                    data-testid="itemFavoriteIcon"
                >
                    <ItemSaveContainer itemId={itemId} />
                </section>
                <ImageRow
                    itemId={itemId}
                    noWidthLimit={noWidthLimit}
                    showBidStatus={showBidStatus}
                    showImageBorder={showImageBorder}
                    showImageWithHover={showImageWithHover}
                />
                <div>
                    {showSocial && isLotOpen && (
                        <ConsoleSocialBadge
                            className={cn('absolute top-0 m-[10px]', {
                                'mx-[10px] my-[20px]': !noWidthLimit,
                            })}
                            pause={paused}
                            viewers={viewers}
                        />
                    )}
                    {showSoldBadge && catalogStatus === AuctionStatus.Done && (
                        <Badge
                            className={cn('absolute left-0 top-0', {
                                'left-xs top-xs': !noWidthLimit,
                            })}
                            color="neutral"
                            emphasis="bold"
                            size="sm"
                        >
                            {formatMessage(['similarSoldItemCarousel', 'badge'])}
                        </Badge>
                    )}
                    <div className="flex justify-between">
                        <TitleRow
                            hideLotNumbers={hideLotNumbers}
                            itemId={itemId}
                            openOnBlank={openOnBlank}
                        />
                        {showSubMenuTrigger && <ItemCardMenu itemId={itemId} />}
                    </div>
                    <DetailsRow
                        allowBidding={allowBidding}
                        hideLiveButton={hideLiveButton}
                        hideSeller={hideSeller}
                        itemId={itemId}
                        showDate={showDate}
                        showEstimate={showEstimate}
                    />
                    <PriceRow itemId={itemId} />
                    <CardCta
                        allowBidding={allowBidding}
                        itemId={itemId}
                        showBanner={showBanner}
                        showCta={showCta}
                    />
                </div>
            </div>
        </div>
    );
};

export const ItemCard = memo<Props>(ItemCardComponent);
